import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { DatasetDetailsProjectsList } from '../components/DatasetDetailsProjectsList';
import { DatasetDetailsTabs } from '../components/DatasetDetailsTabs';
import { DatasetDetails } from '../DatasetDetails.context';

export const DatasetDetailsProjectsTabContainer = observer((): ReactElement => {
  const workspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const datasetDetailsContext = useContext(DatasetDetails);
  const settingsContext = useContext(Settings);
  const { isLoading, withLoaderAsync } = useLoader();
  const { datasetId } = useParams<{ datasetId: string }>();

  useEffect(() => {
    void withLoaderAsync(async () => datasetDetailsContext.service.getDatasetProjectsAsync(datasetId));
  }, [datasetId]);

  const handleOrderingChange = async (orderBy: string, orderType: string): Promise<void> => {
    await withLoaderAsync(async () => datasetDetailsContext.service.changeDatasetProjectsOrder(datasetId, orderBy, orderType));
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await withLoaderAsync(async () => {
      settingsContext.setDatasetDetailsProjectsPageSize(pageSize);
      await datasetDetailsContext.service.changeDatasetProjectsPagination(datasetId, pageNumber, pageSize);
    });
  };

  const imageClickCallback = (): void => {
    datasetDetailsContext.service.setDefaultImagesPaging();
  };

  const { detailsProjects, detailsProjectsPaging, details } = datasetDetailsContext.store;
  const workspaceId = workspaceStore.currentWorkspace.id;

  return (
    <ColumnLayout size='none'>
      <DatasetDetailsTabs
        workspaceId={workspaceId}
        details={details}
        imageClickCallback={imageClickCallback}
      />
      <DatasetDetailsProjectsList
        datasetProjects={detailsProjects.slice()}
        workspaceId={workspaceStore.currentWorkspace.id}
        isLoading={isLoading}
        onSortingChange={handleOrderingChange}
        pagination={{
          pageNumber: detailsProjectsPaging.pageNumber,
          pageSize: settingsContext.store.datasetDetailsProjectsPageSize,
          totalCount: detailsProjectsPaging.totalCount,
          onChange: handlePaginationChange,
        }}
        sorting={{
          orderBy: detailsProjectsPaging.orderBy || '',
          orderType: detailsProjectsPaging.orderType || '',
        }}
      />
    </ColumnLayout>
  );
});
